// NPM Imports
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Switch from "react-toggle-switch";
import moment from "moment";
import { Tooltip, Toastr, Label, Input } from "../../revvComponents";

// Components Imports
import { ConfirmationBox } from "../../commonComponents";
import CloneTemplatePopup from "./CloneTemplatePopup";

// Actions Imports
import { DocumentTemplateActions } from "../../actions";

// Internal Imports
import { PATHS, PERMISSIONS } from "../../constants";
import { GlobalUtils } from "../../utils";

// Destructuring
const {
  toggleTemplateStatus,
  deleteTemplate,
  changeTemplateStatus,
  fetchAllDocumentTemplates,
} = DocumentTemplateActions;
const { checkUserPermission } = GlobalUtils;

// Declarations
const FORM = "form";

class DocTemplateItem extends Component {
  state = {
    showMenu: false,
    templateID: null,
    showCloneTemplatePopup: false,
    showSwitchBtnLoader: false,
  };

  _openCloneTemplatePopup = () => {
    this.setState({ showCloneTemplatePopup: true });
  };

  _closeCloneTemplatePopup = () => {
    this.setState({ showCloneTemplatePopup: false });
  };

  _getTemplateStatusSwitchButton = () => {
    const { template: { status } = {} } = this.props;
    let switchBtn = (
      <Tooltip placement="top" overlay="Activate/Deactivate this template">
        <span
          id="switch-state"
          className="template-status-toggle"
          data-test="doctemplateitem-template-status-toggle">
          <Label
            className="quotedocument-switch"
            data-test="doctemplateitem-template-status-toggle-switch"
            htmlFor="switch-state">
            <Switch
              onClick={() => this._changeTemplateStatus()}
              on={status === "ACTIVE"}
            />
          </Label>
        </span>
      </Tooltip>
    );
    if (status === "DRAFT") {
      switchBtn = (
        <span id="switch-state" className="template-status-toggle">
          <Label className="quotedocument-switch" htmlFor="switch-state">
            <Switch
              onClick={() => {}}
              enabled={false}
              on={status === "ACTIVE"}
            />
          </Label>
        </span>
      );
    }
    if (this.state.showSwitchBtnLoader) {
      return <FontAwesomeIcon icon={["far", "spinner"]} className="fa-spin" />;
    }
    return switchBtn;
  };

  // decide the templates list based on update permission
  _decideTemplateListView = (template, hasUpdatePermission, updatedAt) => {
    const templateIcon =
      template.template_type === FORM ? "file-invoice" : "file-alt";

    if (hasUpdatePermission) {
      return (
        <div
          data-test="doctemplateitem-row-templatecard-items"
          className="row templatecard-items">
          {/* -------------  Template Name ------------- */}
          <div
            data-test="doctemplateitem-row-templatecard-items-template-name"
            className="col-md-3 template-name">
            <Link
              data-test="doctemplateitem-template-link"
              to={`${PATHS.DOCSTEMPLATE_PATH}/${template.id}`}>
              <div data-test="doctemplateitem-template-name">
                <i
                  data-test="doctemplateitem-template-name-ico"
                  className={`template-name-ico ${
                    template.template_type === FORM ? "is-form" : ""
                  }`}>
                  <FontAwesomeIcon icon={["fad", templateIcon]} />
                </i>
                {template.title}
              </div>
            </Link>
          </div>

          {/* -------------  Template Type ------------- */}
          <div className="col-md-3 template-type">
            <span
              className="template-type-tag"
              data-test="doctemplateitem-template-type-tag">
              {template.type.name}
            </span>
          </div>

          {/* -------------  Profile Image of Last Edited ------------- */}
          <div
            className="col-md-2 template-owner document-btns-dp"
            data-test="doctemplateitem-templatelist-template-owner-image">
            <Tooltip placement="bottom" overlay={template.created_by.name}>
              <img
                src={template.created_by.image_url}
                alt="Template Owner Profile"
              />
            </Tooltip>
          </div>

          {/* -------------  Last Edited On ------------- */}
          <div
            className="col-md-2 template-lastupdate"
            data-test="doctemplateitem-template-lastupdated-date">
            {updatedAt}
          </div>

          {/* -------------  Template Status Action ------------- */}
          <div className="col-md-1 template-status">
            {this._getTemplateStatusSwitchButton()}
          </div>

          {/* -------------  Template Delete and Clone Actions ------------- */}
          <div
            className="col-md-1 template-actions template-icons-onhover"
            data-test="doctemplateitem-template-icons-onhover">
            <Tooltip placement="top" overlay="Delete this template">
              <div
                className="btn-icon doctemplateitem-button"
                data-test="doctemplateitem-template-delete-icon"
                role="button"
                tabIndex="0"
                onClick={() =>
                  this._showDeleteTemplateConfirmationBox(template.id)
                }
                onKeyPress={() => {}}>
                <i>
                  <FontAwesomeIcon icon={["far", "trash-alt"]} />
                </i>
              </div>
            </Tooltip>

            <Tooltip placement="top" overlay="Clone this template">
              <div
                className="btn-icon doctemplateitem-icon"
                data-test="doctemplateitem-template-clone-button"
                role="button"
                tabIndex="0"
                onClick={() => this._openCloneTemplatePopup(template.id)}
                onKeyPress={() => {}}>
                <i>
                  <FontAwesomeIcon icon={["far", "clone"]} />
                </i>
              </div>
            </Tooltip>
          </div>
        </div>
      );
    }
    return (
      <div className="row templatecard-items">
        <div className="col-md-3 template-name">
          <i
            className={`template-name-ico ${
              template.template_type === FORM ? "is-form" : ""
            }`}>
            <FontAwesomeIcon icon={["fad", templateIcon]} />
          </i>
          {template.title}
        </div>
        <div className="col-md-3 template-type">
          <span className="template-type-tag">{template.type.name}</span>
        </div>
        <div className="col-md-2 template-owner document-btns-dp">
          <Tooltip placement="bottom" overlay={template.created_by.name}>
            <img
              src={template.created_by.image_url}
              alt="Template Owner Profile"
            />
          </Tooltip>
        </div>
        <div className="col-md-2 template-lastupdate">{updatedAt}</div>
        <div className="col-md-1 template-status">
          <Tooltip
            placement="top"
            overlay={template.Active ? "Active Template" : "Deactive Template"}>
            <span className="col-md-2 is-last">
              <Input
                checked={template.Active}
                className="workflowlist-switch"
                isSwitch
                disabled
                readOnly
                type="checkbox"
                value=""
              />
            </span>
          </Tooltip>
        </div>
      </div>
    );
  };

  _closeConfirmDeletePopup = () => {
    this.setState({ showMenu: false });
  };

  _showDeleteTemplateConfirmationBox = templateID => {
    this.setState({ showMenu: true, templateID });
  };

  _deleteTemplate = () => {
    const { templateID } = this.state;
    this.props
      .deleteTemplate(templateID)
      .then(() => {
        this.setState({ showMenu: false });
        Toastr.showToastr({
          type: "success",
          message: "Template has been deleted",
          timeout: 2000,
        });
      })
      .catch(error => {
        console.error(`DocTemplateItem => _deleteTemplate => ${error}`);
        Toastr.showToastr({
          type: "failure",
          message: "Something went wrong. Please try again!",
          timeout: 3000,
        });
      });
  };

  _changeTemplateStatus = () => {
    const payload = {
      status: this.props.template.status === "ACTIVE" ? "DEACTIVE" : "ACTIVE",
      template_type: "",
    };
    const status =
      this.props.template.status === "ACTIVE" ? "deactivated" : "activated";
    const { title } = this.props.template;
    this.setState({ showSwitchBtnLoader: true });
    this.props
      .changeTemplateStatus(this.props.template.id, payload, true)
      .then(() => {
        this.setState({ showSwitchBtnLoader: false });
        Toastr.showToastr({
          type: "success",
          message: `Your template ${title} has been ${status}.`,
          timeout: 3000,
        });
      })
      .catch(error => {
        this.setState({ showSwitchBtnLoader: false });
        console.error(`DocTemplateItem => _changeTemplateStatus => ${error}`);
        Toastr.showToastr({
          type: "failure",
          message: "Something went wrong. Please try again!",
          timeout: 3000,
        });
      });
  };

  _editTemplate(e) {
    e.preventDefault();
    const { showMenu } = this.state;
    console.debug(showMenu);
  }

  _showTemplateOptions(e) {
    e.preventDefault();
    const { showMenu } = this.state;
    this.setState({ showMenu: !showMenu });
  }

  render() {
    const { template } = this.props;
    const { showCloneTemplatePopup } = this.state;
    const updatedAt = moment(template.updated_at).format("lll");

    return (
      <Fragment>
        <ConfirmationBox
          show={this.state.showMenu}
          closePopup={this._closeConfirmDeletePopup}
          header={`Are you sure you want to delete template: '${template.title}' ?`}
          subHeader={`Once deleted, you won't be able to access this template.`}
          checkboxLabel="Yes, I want to delete."
          data={template.title}
          confirmAction={this._deleteTemplate}
          okBtnText="Delete template"
        />
        {showCloneTemplatePopup && (
          <CloneTemplatePopup
            closePopup={this._closeCloneTemplatePopup}
            id={template.id}
          />
        )}

        <div
          data-test="doctemplateitem-templatecard"
          className="templatecard"
          id={`teammatecard-${template.id}`}>
          {this._decideTemplateListView(
            template,
            checkUserPermission(PERMISSIONS.TEMPLATES.READ_TEMPLATES),
            updatedAt,
          )}
        </div>
      </Fragment>
    );
  }
}

const mapDispatchToProps = {
  toggleTemplateStatus: id => toggleTemplateStatus(id),
  deleteTemplate: id => deleteTemplate(id),
  changeTemplateStatus: (id, data) => changeTemplateStatus(id, data, true),
  fetchAllDocumentTemplates,
};

export default connect(null, mapDispatchToProps)(DocTemplateItem);
